import { useAtomValue, useSetAtom } from 'jotai';
import { DialogTrigger } from 'react-aria-components';

import { Button, Divider, Drawer, Group, Stack } from '@ekhodealer/ekho-common/rev';

import { ErrorBoundaryWrapper } from '@common/components/ErrorBoundaryWrapper';

import { useGetInventory } from '../../api/useGetInventory';
import { closeFiltersDrawerAtom,isFiltersDrawerOpenAtom } from '../../atoms/filtersDrawerAtoms';
import { appliedFiltersCountAtom, inventoryParamsAtom,resetAllFiltersAtom } from '../../atoms/inventoryAtoms';

import { ConditionSection } from './sections/ConditionSection';
import { ImportJobSection } from './sections/ImportJobSection';
import { MatchStatusSection } from './sections/MatchingStatusSection';
import { OnlineSalesSection } from './sections/OnlineSalesSection';
import { PublishStatusSection } from './sections/PublishStatusSection';
import { VehicleInformationSection } from './sections/VehicleInformationSection';
import { InventoryFiltersAppliedSection } from './InventoryFiltersAppliedSection';

import './FiltersDrawer.scss';

// Empty fallback component that renders nothing when import jobs filter fails
const EmptyFallback = () => null;

export const FiltersDrawer = () => {
	// Drawer state atoms
	const isFiltersDrawerOpen = useAtomValue(isFiltersDrawerOpenAtom);
	const closeFiltersDrawer = useSetAtom(closeFiltersDrawerAtom);
	const resetAllFilters = useSetAtom(resetAllFiltersAtom);
	const appliedFiltersCount = useAtomValue(appliedFiltersCountAtom);

	// Get total count for footer
	const params = useAtomValue(inventoryParamsAtom);
	const { data, isLoading } = useGetInventory(params);
	const queryCount = data?.count ?? 0;

	const handleViewResults = () => {
		closeFiltersDrawer();
	};

	const handleClearAll = () => {
		resetAllFilters();
	};

	return (
		<DialogTrigger
			isOpen={isFiltersDrawerOpen}
			onOpenChange={(open) => {
				if (!open) {
					closeFiltersDrawer();
				}
			}}>
			<Drawer
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Inventory Filters'
				className='filters-drawer--custom'>
				<Drawer.Header>Filters</Drawer.Header>
				<Drawer.Content>
					<Stack padding={{ x: 24, top: 12 }}>
						<Stack itemsSpacing='24'>
							{/* Applied Filters Section */}
							<InventoryFiltersAppliedSection />

							{/* Filter Accordions */}
							<Stack>
								<Divider />
								<PublishStatusSection />
								<Divider />
								<MatchStatusSection />
								<Divider />
								<OnlineSalesSection />
								<Divider />
								<ConditionSection />
								<Divider />
								<VehicleInformationSection />
								<ErrorBoundaryWrapper fallbackComponent={EmptyFallback}>
									<ImportJobSection />
									<Divider />
								</ErrorBoundaryWrapper>
							</Stack>
						</Stack>
					</Stack>
				</Drawer.Content>
				<Drawer.Footer>
					<Group itemsAlignX='stretch' itemsSpacing='8' noWrap>
						{appliedFiltersCount > 0 && (
							<Group.Item grow={1}>
								<Button
									variant='secondary'
									size='large'
									onPress={handleClearAll}>
									Clear all
								</Button>
							</Group.Item>
						)}
						<Group.Item grow={appliedFiltersCount > 0 ? 3 : 1}>
							<Button size='large' isFullWidth onPress={handleViewResults}>
								View results {isLoading ? '-' : `(${queryCount})`}
							</Button>
						</Group.Item>
					</Group>
				</Drawer.Footer>
			</Drawer>
		</DialogTrigger>
	);
};
